import React, { useState, useEffect } from 'react';
import { Clock, CheckCircle, Truck, Loader2, ChevronDown, ChevronUp, Phone, Mail, MapPin } from 'lucide-react';
import { AdminLayout } from './AdminDashboard';
import { API } from '../../App';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/orders`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to fetch orders');
      
      const data = await response.json();
      setOrders(data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    setUpdatingStatus(orderId);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update status');
      }

      toast.success(`Order status updated to ${newStatus}`);
      fetchOrders();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(error.message || 'Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Pending': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'Confirmed': return <CheckCircle className="w-4 h-4 text-blue-500" />;
      case 'Delivered': return <Truck className="w-4 h-4 text-green-500" />;
      default: return null;
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Pending': return 'bg-yellow-100 text-yellow-700';
      case 'Confirmed': return 'bg-blue-100 text-blue-700';
      case 'Delivered': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout activeTab="orders">
      <div data-testid="admin-orders">
        <h1 className="font-heading text-2xl font-semibold text-[#1A1A1A] mb-8">Orders</h1>

        {loading ? (
          <div className="bg-white p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#C4A882]" />
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-white p-8 text-center">
            <p className="font-body text-[#666666]">No orders yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div key={order.id} className="bg-white" data-testid={`order-card-${order.order_id}`}>
                {/* Order Header */}
                <div 
                  className="p-6 cursor-pointer"
                  onClick={() => setExpandedOrder(expandedOrder === order.id ? null : order.id)}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div>
                        <p className="font-heading text-lg font-semibold text-[#1A1A1A]">{order.order_id}</p>
                        <p className="font-body text-sm text-[#666666]">{order.customer_name}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-heading text-lg font-bold text-[#1A1A1A]">
                          ₹{order.total.toLocaleString('en-IN')}
                        </p>
                        <p className="font-body text-xs text-[#666666]">{formatDate(order.created_at)}</p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm ${getStatusStyle(order.status)}`}>
                          {getStatusIcon(order.status)}
                          {order.status}
                        </span>
                        {expandedOrder === order.id ? (
                          <ChevronUp className="w-5 h-5 text-[#666666]" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-[#666666]" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded Order Details */}
                {expandedOrder === order.id && (
                  <div className="border-t border-[#E5E5E5] p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Customer Info */}
                      <div>
                        <h3 className="font-heading text-sm font-semibold text-[#1A1A1A] mb-3">Customer Details</h3>
                        <div className="space-y-2">
                          <p className="font-body text-sm flex items-center text-[#666666]">
                            <Phone className="w-4 h-4 mr-2" />
                            {order.phone}
                          </p>
                          <p className="font-body text-sm flex items-center text-[#666666]">
                            <Mail className="w-4 h-4 mr-2" />
                            {order.email}
                          </p>
                          <p className="font-body text-sm flex items-start text-[#666666]">
                            <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                            <span>
                              {order.address}<br />
                              {order.city} - {order.pincode}
                            </span>
                          </p>
                        </div>
                      </div>

                      {/* Order Items */}
                      <div>
                        <h3 className="font-heading text-sm font-semibold text-[#1A1A1A] mb-3">Order Items</h3>
                        <div className="space-y-2">
                          {order.items.map((item, index) => (
                            <div key={index} className="flex items-center gap-3">
                              <img 
                                src={item.image_urls && item.image_urls.length > 0 ? item.image_urls[0] : (item.image_url || 'https://via.placeholder.com/40x40?text=No+Image')} 
                                alt={item.product_name}
                                className="w-10 h-10 object-cover"
                              />
                              <div className="flex-1">
                                <p className="font-body text-sm text-[#1A1A1A] line-clamp-1">{item.product_name}</p>
                                <p className="font-body text-xs text-[#666666]">
                                  Qty: {item.quantity} × ₹{item.price.toLocaleString('en-IN')}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="mt-4 pt-4 border-t border-[#E5E5E5]">
                          <div className="flex justify-between font-body text-sm">
                            <span className="text-[#666666]">Subtotal</span>
                            <span>₹{order.subtotal.toLocaleString('en-IN')}</span>
                          </div>
                          <div className="flex justify-between font-body text-sm mt-1">
                            <span className="text-[#666666]">Delivery</span>
                            <span>₹{order.delivery_charge.toLocaleString('en-IN')}</span>
                          </div>
                          <div className="flex justify-between font-heading text-base font-semibold mt-2">
                            <span>Total</span>
                            <span>₹{order.total.toLocaleString('en-IN')}</span>
                          </div>
                        </div>
                      </div>

                      {/* Status Update */}
                      <div>
                        <h3 className="font-heading text-sm font-semibold text-[#1A1A1A] mb-3">Update Status</h3>
                        <Select 
                          value={order.status} 
                          onValueChange={(value) => handleStatusUpdate(order.order_id, value)}
                          disabled={updatingStatus === order.order_id}
                        >
                          <SelectTrigger className="rounded-none" data-testid={`status-select-${order.order_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Pending">
                              <span className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-yellow-500" />
                                Pending
                              </span>
                            </SelectItem>
                            <SelectItem value="Confirmed">
                              <span className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-blue-500" />
                                Confirmed
                              </span>
                            </SelectItem>
                            <SelectItem value="Delivered">
                              <span className="flex items-center gap-2">
                                <Truck className="w-4 h-4 text-green-500" />
                                Delivered
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        
                        {updatingStatus === order.order_id && (
                          <p className="font-body text-xs text-[#666666] mt-2 flex items-center">
                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            Updating...
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminOrders;
